import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCockpitApi } from '@/api/provider';
import type { AdminTunables, MailConfigInput } from '@/api/cockpit';

const DEFAULT_STALE = 30_000;

export function useAdminState() {
  const api = useCockpitApi();
  return useQuery({
    queryKey: ['cockpit', 'admin'],
    queryFn: () => api.adminState(),
    staleTime: DEFAULT_STALE,
  });
}

export function useAdminSaveMailConfig() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: MailConfigInput) => api.saveMailConfig(values),
    onSuccess: (data) => {
      qc.setQueryData(['cockpit', 'admin'], data);
    },
  });
}

export function useAdminResetMail() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.resetMail(),
    onSuccess: (data) => {
      qc.setQueryData(['cockpit', 'admin'], data);
    },
  });
}

export function useAdminUpdateTunables() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: AdminTunables) => api.updateTunables(values),
    onSuccess: (data) => {
      qc.setQueryData(['cockpit', 'admin'], data);
    },
  });
}

export function useAdminActivateVersion() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.activateVersion(id),
    onSuccess: (data) => {
      qc.setQueryData(['cockpit', 'admin'], data);
    },
  });
}

export function useAdminRunCustomerSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runCustomerSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunProductSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runProductSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunExactProductSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemNumber: string) => api.runExactProductSync(itemNumber),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunLegalEntitySync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runLegalEntitySync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunSiteSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runSiteSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunPaymentTermsSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runPaymentTermsSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunDeliveryTermsSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runDeliveryTermsSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunDeliveryModeSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runDeliveryModeSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunUnitOfMeasureSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runUnitOfMeasureSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminRunEmbeddingSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runEmbeddingSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminResetSync() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: string) => api.resetSync(target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'admin'] });
    },
  });
}

export function useAdminTriggerStagingBatch() {
  const api = useCockpitApi();
  return useMutation({
    mutationFn: (dataAreaId: string) => api.triggerStagingBatchJob(dataAreaId),
  });
}
